import React, { useState, useEffect, useMemo } from 'react';
import { Button, message } from 'antd';
import { AuditOutlined, PaperClipOutlined, RollbackOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DOLayout from '../../components/directOrders/DOLayout';
import IconTile from '../../components/directOrders/IconTile';
import OrdersExplorer from '../../components/directOrders/OrdersExplorer';
import ReuploadModal from '../../components/directOrders/ReuploadModal';
import DirectOrderViewModal from '../../components/DirectOrderViewModal';
import DirectOrderEditModal from '../../components/DirectOrderEditModal';
import { useDirectOrder } from '../../context/DirectOrderContext';
import { useAuth } from '../../context/AuthContext';
import { API_ORIGIN } from '../../config';
import { canUserApprove, getOrderStatus, getReference, getStatusLabel } from '../../utils/directOrderStatus';

const COLLAPSED_ITEMS = 6;

const DirectOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const {
    isAdmin,
    isApprover,
    userDirectOrders,
    allDirectOrders,
    missingAttachments,
    loading,
    loadUserDirectOrders,
    loadAllDirectOrders,
    loadMissingAttachments,
  } = useDirectOrder();

  const canReview = Boolean(user?.isDirectOrderAdmin) && (isAdmin || isApprover);

  const [reuploadOrder, setReuploadOrder] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [reuploadLoading, setReuploadLoading] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [showAllTodo, setShowAllTodo] = useState(false);

  useEffect(() => {
    if (user?.email) {
      loadUserDirectOrders();
      loadMissingAttachments();
    }
  }, [user?.email, loadUserDirectOrders, loadMissingAttachments]);

  useEffect(() => {
    if (canReview) {
      loadAllDirectOrders();
    }
  }, [canReview, loadAllDirectOrders]);

  // "Missing files" in the index links to #todo
  useEffect(() => {
    if (location.hash === '#todo') {
      document.getElementById('todo')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 10) {
      message.error('Maximum 10 files allowed');
      return;
    }
    setUploadingFiles(files);
  };

  const closeReupload = () => {
    setReuploadOrder(null);
    setUploadingFiles([]);
  };

  const handleReuploadAttachments = async () => {
    if (!reuploadOrder || uploadingFiles.length === 0) {
      message.error('Please select files to upload');
      return;
    }

    setReuploadLoading(true);
    try {
      const formData = new FormData();
      uploadingFiles.forEach((file) => formData.append('attachments', file));

      const response = await fetch(`${API_ORIGIN}/api/direct-order/reupload-attachments/${reuploadOrder.doId}`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (response.ok && data.success) {
        message.success(`Attachments replaced for ${data.doRef}`);
        closeReupload();
        loadMissingAttachments();
      } else {
        message.error(data.error || 'Failed to re-upload attachments');
      }
    } catch (error) {
      message.error('Failed to re-upload attachments');
    } finally {
      setReuploadLoading(false);
    }
  };

  const awaitingMyApproval = useMemo(
    () => (canReview ? allDirectOrders.filter((order) => canUserApprove(order, user?.email)).length : 0),
    [canReview, allDirectOrders, user?.email]
  );

  const rejectedOrders = useMemo(
    () => userDirectOrders.filter((order) => getOrderStatus(order) === 'rejected'),
    [userDirectOrders]
  );

  // Everything the user has to act on, in one ruled list
  const todo = [
    ...(awaitingMyApproval > 0
      ? [
          {
            key: 'approve',
            tone: 'accent',
            icon: <AuditOutlined />,
            title: `${awaitingMyApproval} ${awaitingMyApproval === 1 ? 'order is' : 'orders are'} waiting for your approval`,
            meta: 'In the approvals queue',
            action: (
              <Button type="primary" onClick={() => navigate('/direct-order/dashboard?view=pending')}>
                Review
              </Button>
            ),
          },
        ]
      : []),
    ...rejectedOrders.map((order) => ({
      key: `rejected-${order.doId}`,
      tone: 'bad',
      icon: <RollbackOutlined />,
      title: order.doSupplier || 'Unknown supplier',
      meta: `${getReference(order)} · ${getStatusLabel(order)}`,
      onOpen: () => setViewOrder(order),
      action: <Button onClick={() => setEditOrder(order)}>Edit and resubmit</Button>,
    })),
    ...missingAttachments.map((order) => ({
      key: `files-${order.doId}`,
      tone: 'warn',
      icon: <PaperClipOutlined />,
      title: order.doSupplier || 'Unknown supplier',
      meta: `DO-${order.doRef} · Attachments missing`,
      action: <Button onClick={() => setReuploadOrder(order)}>Upload files</Button>,
    })),
  ];
  const visibleTodo = showAllTodo ? todo : todo.slice(0, COLLAPSED_ITEMS);
  const initialLoad = loading && userDirectOrders.length === 0;

  return (
    <DOLayout title="Overview">
      {todo.length > 0 && (
        <section className="do-block" id="todo" aria-labelledby="do-todo-title">
          <header className="do-block__head">
            <h2 className="do-block__title" id="do-todo-title">To do</h2>
            <span className="do-block__meta do-num">{todo.length}</span>
          </header>
          <ul className="do-todo">
            {visibleTodo.map((item) => (
              <li className="do-todo__item" key={item.key}>
                <IconTile icon={item.icon} tone={item.tone} />
                <div className="do-todo__text">
                  {item.onOpen ? (
                    <button type="button" className="do-todo__title" onClick={item.onOpen}>
                      {item.title}
                    </button>
                  ) : (
                    <span className="do-todo__title">{item.title}</span>
                  )}
                  <span className="do-todo__meta">{item.meta}</span>
                </div>
                <div className="do-todo__action">{item.action}</div>
              </li>
            ))}
          </ul>
          {todo.length > COLLAPSED_ITEMS && (
            <button type="button" className="do-link do-programme__more" onClick={() => setShowAllTodo((v) => !v)}>
              {showAllTodo ? 'Show fewer' : `Show all ${todo.length}`}
            </button>
          )}
        </section>
      )}

      <section className="do-block" aria-labelledby="do-mine-title">
        <header className="do-block__head">
          <h2 className="do-block__title" id="do-mine-title">Your orders by festival</h2>
          {userDirectOrders.length > 0 && (
            <Link to="/direct-order/view" className="do-link">Open full list</Link>
          )}
        </header>
        <OrdersExplorer
          compact
          orders={userDirectOrders}
          loading={initialLoad}
          onOpen={setViewOrder}
          emptyTitle="No direct orders yet"
          emptyHint="Orders you raise will be listed here, grouped by festival."
        />
      </section>

      <ReuploadModal
        order={reuploadOrder}
        files={uploadingFiles}
        onFilesChange={handleFileChange}
        onOk={handleReuploadAttachments}
        onCancel={closeReupload}
        loading={reuploadLoading}
      />

      {viewOrder && <DirectOrderViewModal visible directOrder={viewOrder} onClose={() => setViewOrder(null)} />}
      {editOrder && (
        <DirectOrderEditModal
          visible
          directOrder={editOrder}
          onClose={() => setEditOrder(null)}
          onSave={() => {
            setEditOrder(null);
            loadUserDirectOrders();
          }}
        />
      )}
    </DOLayout>
  );
};

export default DirectOrder;
